import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calculator as CalculatorIcon, Info } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import Calculator from '../components/Calculator';
import Input from '../components/Input';
import SEOHead from '../components/SEOHead';

// Chattel mortgage = business car finance secured by the vehicle (the chattel),
// commonly with a balloon. Repayment maths are the same annuity-with-residual as
// a balloon loan; the business angle is the GST input tax credit on the purchase
// and the tax deductibility of interest + depreciation (not modelled — noted).
const monthlyWithResidual = (principal: number, annualRate: number, months: number, residual: number): number => {
  const i = annualRate / 100 / 12;
  if (i === 0) return (principal - residual) / months;
  const discount = Math.pow(1 + i, -months);
  const annuity = (1 - discount) / i;
  return (principal - residual * discount) / annuity;
};

const ChattelMortgageCalculator: React.FC = () => {
  const [vehiclePrice, setVehiclePrice] = useState('50000');
  const [deposit, setDeposit] = useState('0');
  const [interestRate, setInterestRate] = useState('7.50');
  const [loanTerm, setLoanTerm] = useState('60');
  const [balloonPct, setBalloonPct] = useState('20');

  const [monthly, setMonthly] = useState(0);
  const [balloonAmount, setBalloonAmount] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);
  const [gstCredit, setGstCredit] = useState(0);
  const [amountFinanced, setAmountFinanced] = useState(0);

  useEffect(() => {
    const price = parseFloat(vehiclePrice);
    const dep = parseFloat(deposit || '0');
    const rate = parseFloat(interestRate);
    const n = parseInt(loanTerm, 10);
    const pct = parseFloat(balloonPct);
    if (isNaN(price) || price <= 0 || isNaN(dep) || dep < 0 || dep >= price || isNaN(rate) || rate < 0 || isNaN(n) || n <= 0 || isNaN(pct) || pct < 0 || pct >= 100) {
      setMonthly(0);
      return;
    }
    const financed = price - dep;
    const B = financed * (pct / 100);
    const m = monthlyWithResidual(financed, rate, n, B);
    setAmountFinanced(financed);
    setBalloonAmount(B);
    setMonthly(m);
    setTotalInterest(m * n + B - financed);
    setGstCredit(price / 11); // GST component of a GST-inclusive price
  }, [vehiclePrice, deposit, interestRate, loanTerm, balloonPct]);

  const fmt = (v: number): string =>
    new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 2 }).format(v);

  return (
    <>
      <SEOHead
        title="Chattel Mortgage Calculator Australia | Business Car Finance"
        description="Calculate repayments on a chattel mortgage for a business vehicle in Australia. See monthly repayments, balloon amount, total interest and the GST input tax credit on the purchase."
        keywords="chattel mortgage calculator australia, business car loan calculator, commercial vehicle finance, chattel mortgage balloon, business vehicle gst credit, abn car finance"
      />
      <Helmet>
        <script type="application/ld+json">
          {`
            {
              "@context": "https://schema.org",
              "@type": "FinancialProduct",
              "name": "Chattel Mortgage Calculator",
              "description": "Calculate chattel mortgage repayments for business vehicle finance in Australia, including balloon and GST input tax credit.",
              "category": "LoanOrCredit",
              "feesAndCommissionsSpecification": "Free online calculator",
              "areaServed": { "@type": "Country", "name": "Australia" },
              "interactivityType": "active"
            }
          `}
        </script>
      </Helmet>
      <Calculator
        title="Chattel Mortgage Calculator (Business Car Finance)"
        description="Work out monthly repayments, the balloon amount and total interest on a chattel mortgage for a business vehicle — plus the GST input tax credit you can claim on the purchase."
        icon={CalculatorIcon}
        result={monthly > 0 ? `Monthly repayment: ${fmt(monthly)}` : undefined}
      >
        <div className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-md mb-6">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-700">
                A chattel mortgage is business car finance secured by the vehicle. If you're registered for GST, you can generally
                claim the GST on the purchase price as an input tax credit, and the interest and depreciation may be tax deductible for
                the business-use portion. Speak to your accountant about your situation.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <Input label="Vehicle Price (inc GST)" value={vehiclePrice} onChange={setVehiclePrice} unit="$" placeholder="e.g. 50000" />
            <Input label="Deposit / Trade-in" value={deposit} onChange={setDeposit} unit="$" placeholder="e.g. 0" />
            <Input label="Interest Rate (p.a.)" value={interestRate} onChange={setInterestRate} unit="%" placeholder="e.g. 7.50" />
            <div className="form-control w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1">Loan Term</label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-200"
                value={loanTerm}
                onChange={(e) => setLoanTerm(e.target.value)}
              >
                {[12, 24, 36, 48, 60, 72, 84].map((m) => (
                  <option key={m} value={m}>{m} months ({m / 12} years)</option>
                ))}
              </select>
            </div>
            <Input label="Balloon / Residual (% of financed)" value={balloonPct} onChange={setBalloonPct} unit="%" placeholder="e.g. 20" />
          </div>

          {monthly > 0 && (
            <div className="mt-8 bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden">
              <div className="bg-blue-600 text-white px-6 py-4">
                <h2 className="text-xl font-semibold">Chattel Mortgage Summary</h2>
              </div>
              <div className="p-6 grid md:grid-cols-2 gap-4">
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Amount financed</span><span className="font-medium">{fmt(amountFinanced)}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Monthly repayment</span><span className="font-bold text-blue-700">{fmt(monthly)}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Balloon at end</span><span className="font-medium">{fmt(balloonAmount)}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">Total interest</span><span className="font-medium">{fmt(totalInterest)}</span></div>
                <div className="flex justify-between border-b py-2"><span className="text-gray-600">GST input tax credit (approx.)</span><span className="font-medium">{fmt(gstCredit)}</span></div>
              </div>
            </div>
          )}

          <div className="mt-8 bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
            <h2 className="text-xl font-semibold text-gray-800">Frequently asked questions</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Who can get a chattel mortgage?</h3>
                <p className="text-gray-600">Businesses (including sole traders with an ABN) buying a vehicle used mainly for business. Predominantly private buyers usually use a standard{' '}
                  <Link className="text-blue-600 hover:underline" to="/car-loan-calculator/">car loan</Link> instead.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Can I claim the GST?</h3>
                <p className="text-gray-600">If you're registered for GST, you can generally claim the GST on the purchase price as an input tax credit in your next BAS (subject to business-use apportionment and the car limit). This calculator shows the GST component as a guide.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">Is the interest tax deductible?</h3>
                <p className="text-gray-600">The interest and depreciation are typically deductible for the business-use portion. Confirm with your accountant.</p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-700">How does the balloon work?</h3>
                <p className="text-gray-600">A balloon defers part of the amount financed to the end of the term, lowering repayments. See the detail on our{' '}
                  <Link className="text-blue-600 hover:underline" to="/balloon-payment-calculator/">balloon payment calculator</Link>.</p>
              </div>
            </div>
            <p className="text-xs text-gray-500 pt-2">Estimates only and not tax or financial advice. GST claims are subject to the ATO car cost limit and business-use rules. Confirm figures with your lender and accountant.</p>
          </div>
        </div>
      </Calculator>
    </>
  );
};

export default ChattelMortgageCalculator;
